import { AsyncPipe, NgClass } from '@angular/common';
import { AfterViewInit, Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ButtonModule } from '@bcef/ia-components/buttons';
import { IconsModule } from '@bcef/ia-ng-icons';
import { Observable } from 'rxjs';

import { DocumentUploadService } from '@shared/components/upload';
import { DOCUMENT_SERVICE } from '@shared/tokens/document-upload-service.token';
import { SafePipe } from '@shared/utils';

import { DragAndDropDirective } from '../../directives/drag-and-drop.directive';
import {
  DocumentForm,
  DocumentItem,
  DocumentLabels,
  DocumentMimeType,
} from './document-upload-file.interface';

@Component({
  selector: 'ia-succession-document-upload-area',
  templateUrl: './document-upload-area.component.html',
  imports: [AsyncPipe, ButtonModule, DragAndDropDirective, IconsModule, NgClass, SafePipe],
  standalone: true,
})
export class DocumentUploadAreaComponent implements OnInit, AfterViewInit {
  private readonly documentService: DocumentUploadService = inject(DOCUMENT_SERVICE);

  public title: string = this.documentService.title;

  public description?: string = this.documentService.description;

  public maxNumberOfDocuments: number = this.documentService.maxNumberOfDocuments;

  public hasDocuments: boolean = this.documentService.hasDocuments;

  public isProcessing$: Observable<boolean> = this.documentService.isProcessing$;

  public hasApiError$: Observable<boolean> = this.documentService.hasApiError$;

  public preview$: Observable<DocumentItem | undefined> = this.documentService.preview$;

  public documents$: Observable<DocumentItem[]> = this.documentService.documents$;

  public form: FormGroup<DocumentForm> = this.documentService.form;

  public labels: DocumentLabels = this.documentService.labels;

  @ViewChild('fileInput', { static: false }) public fileInput!: ElementRef;

  public readonly DocumentMimeType = DocumentMimeType;

  public get fileError(): string | null {
    if (typeof this.labels.fileError === 'string') {
      return this.labels.fileError.replace(
        '[[FILENAME]]',
        this.form.controls.document.value?.name || '',
      );
    }
    if (this.form.controls.document.errors) {
      const errors: string[] = Object.keys(this.form.controls.document.errors);
      const error = errors.find(
        (item) => !!(this.labels.fileError as Record<string, string>)[item],
      );
      if (error) {
        return this.labels.fileError[error];
      } else {
        return this.labels.fileError['default'].replace(
          '[[FILENAME]]',
          this.form.controls.document.value?.name || '',
        );
      }
    }

    return null;
  }

  public ngOnInit(): void {
    this.form.valueChanges.subscribe(() => {
      if (this.form.valid) {
        this.documentService.upload(this.form.controls.document.value as File);
      }
    });
  }

  public ngAfterViewInit(): void {
    this.documentService.registerFileInput(this.fileInput);
  }

  public onFileDropped($event: FileList): void {
    this.prepareFilesList(Object.entries($event).map(([, file]: [string, File]) => file));
  }

  public fileBrowseHandler($event: Event): void {
    this.prepareFilesList(
      Object.entries(($event.target as HTMLInputElement).files ?? {}).map(
        ([, file]: [string, File]) => file,
      ),
    );
  }

  public prepareFilesList(files: File[]): void {
    this.form.controls.document.patchValue(files[0]);
    this.form.controls.document.markAsTouched();
  }

  public onSelectDocument(document: DocumentItem): void {
    this.documentService.select(document);
  }

  public onRemoveDocument(document: DocumentItem): void {
    this.documentService.delete(document);
  }

  public onNewDocument(): void {
    this.documentService.newDocument();
  }

  public onSaveDocuments(): void {
    this.documentService.saveDocuments();
  }
}
